import { EventType } from "@/analytics/analytics-events";
import {
  computeToolPropertyForSelectLocationEvent,
  SelectLocationProperties,
  SelectLocationSources,
} from "@/analytics/generic-events-properties";
import {
  WalkPathsRenderer,
  type WayPointTarget,
} from "@/components/r3f/renderers/odometry-paths/walk-paths-renderer";
import { OrthoMosaicRenderer } from "@/components/r3f/renderers/orthomosaic-renderer";
import { PhotoRenderer } from "@/components/r3f/renderers/photo-render";
import type { WaypointPosition } from "@/components/r3f/renderers/waypoint-label-render";
import { useClippingPlanes } from "@/hooks/use-clipping-planes";
import { useDetectTooManyRedraws } from "@/hooks/use-detect-too-many-redraws";
import { useCurrentAreaClippingBox } from "@/hooks/use-object-bounding-box";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { useAppSelector, useAppStore } from "@/store/store-hooks";
import type { ActiveToolAndPicking } from "@/tools/picking-tools";
import { usePickingToolsCallbacks } from "@/tools/use-picking-tools-callbacks";
import { isIElementOrthomosaic } from "@/types/ielement-extra-types";
import { SceneFilter } from "@/types/scene-filter";
import {
  Map2DControls,
  selectAncestor,
  selectChildDepthFirst,
  selectIElement,
  UniformLights,
  useNonExhaustiveEffect,
} from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  isIElementImg2d,
  isIElementImg360,
  isIElementOdometryPathSection,
  isIElementRoomSection,
  isIElementSectionFlash,
  isIElementTimeTravelSection,
  isIElementVideoRecording,
  isIElementVideoRecordingTimeseries,
  isSpaceAnnotation,
  type IElement,
  type IElementGenericImgSheet,
  type IElementGenericOdometryPath,
  type IElementImageCommand,
  type IElementImg360,
} from "@faro-lotv/ielement-types";
import { useThree } from "@react-three/fiber";
import { createSelector } from "@reduxjs/toolkit";
import { useCallback, useMemo, useState, type JSX } from "react";
import { Box3, type Quaternion, type Vector3 } from "three";
import { selectBestModelCameraFor360 } from "./animations/pano-to-model";
import { usePickClosestWaypoint } from "./hooks/use-pick-closest-waypoints";
import { WalkControls, type WalkControlsProps } from "./walk-controls";
import { WalkSceneCore, type WalkSceneCoreProps } from "./walk-scene-core";
import { WalkTools } from "./walk-tools";
import {
  sceneFilterFromElement,
  type WalkSceneActiveElement,
  type WalkSceneOverlayElement,
} from "./walk-types";

export type WalkSceneProps = Pick<
  WalkSceneCoreProps,
  "onUserWalkedTo" | "onCameraMovedViaAnimation" | "onPanoTransformChanged"
> &
  Pick<
    WalkSceneCoreProps,
    "placeholders" | "shouldShowPlaceholders" | "viewType" | "annotations"
  > &
  /** The properties for the controls */
  Pick<WalkControlsProps, "onUserInteracted"> & {
    /** Current active element for this scene */
    activeElement: WalkSceneActiveElement;

    /** sheet the user is walking on */
    sheetForElevation?: IElementGenericImgSheet;

    /** All paths to show */
    paths: IElementGenericOdometryPath[];

    /** Element to render together with the active one */
    overlayElement?: WalkSceneOverlayElement;

    /** if provided quaternion should be applied while performing pano animation */
    targetQuaternion?: Quaternion;

    /** The 2D photo object (image command) to look at */
    photoObject?: IElementImageCommand;

    /** Callback executed when the local active element inside the walk scene has changed. */
    onActiveElementChanged?(element: IElement): void;

    /** Callback called when the active waypoint target element inside the walk scene has changed. */
    onWayPointChanged(target: WayPointTarget): void;

    /** Callback issued when the camera started a translation. */
    onCameraStartedTranslating?(): void;

    /** Callback issued when the user moved the camera via the exploration controls */
    onCameraMovedViaControls?(pos: Vector3): void;

    /** Callback executed when the animation finishes */
    onAnimationFinished?(active: WalkSceneActiveElement): void;
  };

/**
 * @returns A Scene that handles exploring data at eye level "walking" though @see SceneFilter data.
 */
export function WalkScene({
  activeElement,
  sheetForElevation,
  paths,
  overlayElement,
  placeholders,
  shouldShowPlaceholders,
  annotations,
  targetQuaternion,
  viewType,
  photoObject,
  onUserWalkedTo,
  onCameraMovedViaAnimation,
  onPanoTransformChanged,
  onUserInteracted,
  onActiveElementChanged,
  onWayPointChanged,
  onCameraStartedTranslating,
  onCameraMovedViaControls,
  onAnimationFinished,
}: WalkSceneProps): JSX.Element | null {
  useDetectTooManyRedraws("WalkScene");

  const [currentElement, setCurrentElement] = useState(activeElement);
  const [targetElement, setTargetElement] = useState<WalkSceneActiveElement>();
  const activePath = useAppSelector(selectActivePath(paths, currentElement));

  const animationFinished = useCallback(
    (active: WalkSceneActiveElement) => {
      setCurrentElement(active);
      setTargetElement(undefined);
      onActiveElementChanged?.(active);
      if (onAnimationFinished) onAnimationFinished(active);
    },
    [onActiveElementChanged, onAnimationFinished],
  );

  const sceneFilter = sceneFilterFromElement(currentElement);
  const camera = useThree((s) => s.camera);

  /**
   * We want to trigger animation only once per change the active element.
   * All other dependencies should not trigger animation.
   * In order to avoid too many redundant calls of onWayPointChanged() using here
   * useNonExhaustiveEffect instead of useEffect
   */
  useNonExhaustiveEffect(() => {
    if (activeElement !== currentElement && !isIElementImg2d(activeElement)) {
      setTargetElement(activeElement);
    }
  }, [activeElement]);

  const store = useAppStore();

  const spaceAnnotations = useMemo(
    () => annotations.filter(isSpaceAnnotation),
    [annotations],
  );

  const { tools, pointerMovePreview, ...toolEvents } =
    usePickingToolsCallbacks();

  const [activeToolAndPicking, setActiveToolAndPicking] =
    useState<ActiveToolAndPicking>({
      activeTool: null,
      isPickingToolActive: false,
    });

  const onControlsMovementFinished = useCallback(() => {
    onCameraMovedViaControls?.(camera.position.clone());
  }, [camera, onCameraMovedViaControls]);

  const { clippingPlanes, setClippingPlanes } = useClippingPlanes();

  const areaBox = useCurrentAreaClippingBox();

  const [allModelsBox, setAllModelsBox] = useState<Box3>(new Box3());

  const [clippingBoxChanging, setClippingBoxChanging] = useState(false);

  const activePano = isIElementImg360(currentElement)
    ? currentElement
    : undefined;

  const onWaypointClicked = useCallback(
    (targetElement: IElementImg360) => {
      Analytics.track<SelectLocationProperties>(EventType.selectLocation, {
        via: SelectLocationSources.panoViewPanoClick,
        tool: computeToolPropertyForSelectLocationEvent(
          activeToolAndPicking.activeTool,
        ),
      });
      const position = selectBestModelCameraFor360(
        targetElement,
        sheetForElevation,
      )(store.getState());
      onWayPointChanged({
        targetElement,
        position,
        quaternion: camera.quaternion,
      });
    },
    [
      activeToolAndPicking.activeTool,
      camera.quaternion,
      onWayPointChanged,
      sheetForElevation,
      store,
    ],
  );

  const [waypoints, setWaypoints] = useState<WaypointPosition[]>([]);
  const [pathWaypoints, setPathWaypoints] = useState<WaypointPosition[][]>([]);
  const allWaypoints = useMemo(
    () => [...waypoints, ...pathWaypoints.flat()],
    [pathWaypoints, waypoints],
  );

  const closestWaypoint = usePickClosestWaypoint({
    camera,
    activePano,
    sheetForElevation,
    waypoints: allWaypoints,
    onWaypointClicked,
  });

  const setActiveElement = useCallback(
    (el: IElementImg360 | undefined) => {
      if (!el) return;
      if (
        isIElementImg360(currentElement) ||
        isIElementVideoRecording(currentElement) ||
        isIElementVideoRecordingTimeseries(currentElement)
      ) {
        setTargetElement(el);
      } else {
        onUserWalkedTo(
          selectBestModelCameraFor360(el, sheetForElevation)(store.getState()),
        );
      }
    },
    [currentElement, onUserWalkedTo, sheetForElevation, store],
  );

  if (isIElementImg2d(activeElement)) {
    // set currentElement to avoid possible animation glitch
    if (currentElement !== activeElement) {
      setCurrentElement(activeElement);
    }

    // render 2D photo using orthographic camera and Map2DControls(wrapped in View component)
    return (
      <>
        <PhotoRenderer iElementImg2d={activeElement} viewType={viewType} />
        <Map2DControls camera={camera} />
      </>
    );
  }

  if (isIElementOrthomosaic(activeElement)) {
    // set currentElement to avoid possible animation glitch
    if (currentElement !== activeElement) {
      setCurrentElement(activeElement);
    }

    return <OrthoMosaicRenderer orthoMosaic={activeElement} />;
  }

  return (
    <>
      <UniformLights />
      <WalkSceneCore
        currentElement={currentElement}
        targetElement={targetElement}
        photoObject={photoObject}
        overlayElement={overlayElement}
        highlightedElement={closestWaypoint?.id}
        sheetForElevation={sheetForElevation}
        viewType={viewType}
        placeholders={placeholders}
        shouldShowPlaceholders={shouldShowPlaceholders}
        targetQuaternion={targetQuaternion}
        annotations={spaceAnnotations}
        onAnimationFinished={animationFinished}
        onUserWalkedTo={onUserWalkedTo}
        onCameraMovedViaAnimation={onCameraMovedViaAnimation}
        onPanoTransformChanged={onPanoTransformChanged}
        onTargetElementChanged={setTargetElement}
        onWayPointChanged={onWayPointChanged}
        onRenderedWaypointsChanged={setWaypoints}
        activeToolAndPicking={activeToolAndPicking}
        {...toolEvents}
        clippingPlanes={clippingPlanes}
        renderOnDemand={!clippingBoxChanging}
        onAllModelsBoxChanged={setAllModelsBox}
      />
      <WalkTools
        currentElement={currentElement}
        overlayElement={overlayElement}
        ref={tools}
        onActiveToolChanged={setActiveToolAndPicking}
        areaBox={areaBox}
        modelBox={allModelsBox}
        clippingPlanesChanged={setClippingPlanes}
        clippingBoxChanging={setClippingBoxChanging}
        pointerMovePreview={pointerMovePreview}
      />

      <WalkControls
        onUserInteracted={onUserInteracted}
        canMove={!isIElementImg360(activeElement)}
        onCameraStartedTranslating={onCameraStartedTranslating}
        onCameraStoppedTranslating={onControlsMovementFinished}
      />
      {!activeToolAndPicking.isPickingToolActive && (
        <WalkPathsRenderer
          fadeOff
          activePano={activePano}
          activePath={activePath}
          paths={paths}
          alignmentRequired={sceneFilter === SceneFilter.Pano}
          sheetForElevation={sheetForElevation}
          clippingPlanes={clippingPlanes}
          onWayPointChanged={onWayPointChanged}
          onRenderedWaypointsChanged={setPathWaypoints}
          highlightedElement={closestWaypoint?.id}
          setActiveElement={setActiveElement}
          onWalkToPosition={onUserWalkedTo}
        />
      )}
    </>
  );
}

/**
 * @returns The odometry path to which the current active element belongs, if it exists
 * @param store The iElements state
 * @param paths The list of available paths in the current scene
 * @param pano The 360 image to check
 */
export const selectActivePath = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (store: OnlyIElementsStore, paths: IElementGenericOdometryPath[]) =>
        paths,
      (
        store: OnlyIElementsStore,
        paths: IElementGenericOdometryPath[],
        pano: WalkSceneActiveElement | undefined,
      ) => pano,
    ],
    (iElements, paths, pano) => {
      // Check if there's a parent path
      const path = selectAncestor(
        pano,
        isIElementOdometryPathSection,
      )({ iElements });
      if (path) return paths.find((p) => p.id === path.id);

      // Return the path from which the flash scan was generated
      const flashScan = selectAncestor(
        pano,
        isIElementSectionFlash,
      )({ iElements });
      if (flashScan?.parentId) {
        const parent = selectIElement(flashScan.parentId)({ iElements });
        if (parent) {
          const path = selectChildDepthFirst(
            parent,
            isIElementOdometryPathSection,
          )({ iElements });
          return path;
        }
      }

      // If we are in a video mode location, check which path it was promoted from
      const room = selectAncestor(
        pano,
        (e) => isIElementRoomSection(e) || isIElementTimeTravelSection(e),
      )({ iElements });

      // TODO: add property to cloned rooms mapping the original video mode dataset
      return room
        ? paths.find((path) =>
            selectChildDepthFirst(
              path,
              (e) => isIElementImg360(e) && e.targetId === room.id,
            )({ iElements }),
          )
        : undefined;
    },
  ),
);
